
import { useMemo, type ReactNode } from "react";
import { motion, type Variants } from "framer-motion";
import { useDeck } from "./SectionDeck";

export type RevealDir =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "fade"
  | "scale";

interface RevealProps {
  /** Preferred direction in 'varied' tuner mode. In 'uniform' mode all reveals come from the bottom. */
  dir?: RevealDir;
  delay?: number;
  /** Override the deck's reveal duration (ms). */
  durationOverride?: number;
  /** Override the deck's reveal distance (px). */
  distanceOverride?: number;
  /** Trigger animation amount threshold (0–1). Default 0.25. */
  amount?: number;
  className?: string;
  children: ReactNode;
}

const EASE = [0.16, 1, 0.3, 1] as [number, number, number, number];

function buildVariants(dir: RevealDir, distance: number, duration: number): Variants {
  const t = { duration: duration / 1000, ease: EASE };
  switch (dir) {
    case "left":
      return {
        hidden: { opacity: 0, x: -distance },
        show: { opacity: 1, x: 0, transition: t },
      };
    case "right":
      return {
        hidden: { opacity: 0, x: distance },
        show: { opacity: 1, x: 0, transition: t },
      };
    case "top":
      return {
        hidden: { opacity: 0, y: -distance },
        show: { opacity: 1, y: 0, transition: t },
      };
    case "bottom":
      return {
        hidden: { opacity: 0, y: distance },
        show: { opacity: 1, y: 0, transition: t },
      };
    case "scale":
      return {
        hidden: { opacity: 0, scale: 0.94 },
        show: { opacity: 1, scale: 1, transition: t },
      };
    case "fade":
    default:
      return {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: t },
      };
  }
}

export default function Reveal({
  dir = "bottom",
  delay = 0,
  durationOverride,
  distanceOverride,
  amount = 0.25,
  className,
  children,
}: RevealProps) {
  const { tuner } = useDeck();

  const effectiveDir: RevealDir =
    tuner.varyDir === "uniform" ? "bottom" : dir;

  const duration = durationOverride ?? tuner.revealDuration;
  const distance = distanceOverride ?? tuner.revealDistance;

  const variants = useMemo(
    () => buildVariants(effectiveDir, distance, duration),
    [effectiveDir, distance, duration]
  );

  return (
    <motion.div
      className={className}
      variants={variants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount }}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
}
